export type DataType = 'string' | 'number' | 'boolean' | 'array' | 'object' | 'null';
export type Priority = 'Required' | 'Standard' | 'Optional';

export interface Parameter {
  id: string;
  path: string;
  codeName: string; // The actual property name (last segment of path)
  friendlyName: string;
  description: string;
  dataTypes: DataType[];
  priorities: Priority[];
  regexFormula: string;
  llmInstructions?: string; // New field for LLM instructions
  isPii?: boolean; // New field for PII status
  children?: Parameter[];
  isExpanded?: boolean;
  level?: number;
  isLastChild?: boolean;
  parentPaths?: string[];
  scope: 'global' | 'eventSpecific';
  placeholder?: string;
  fullPath: string; // Store the full path for reference (now required)
  isNew?: boolean; // Flag for newly created parameters
}

export interface EventSchema { // Renamed from EventItem
  id: string;
  name: string; // Friendly name
  codeName: string; // Code name (snake_case)
  description: string;
  parameterFullPaths: string[]; // References properties by their fullPath
  isNew?: boolean; // Flag for newly created events
}

export interface UnifiedSchema { // New interface for the single database entry
  id: string;
  name: string; // e.g., "All Events Schema"
  codeName: string; // Code name for the unified schema (snake_case)
  description: string;
  events: EventSchema[]; // This will hold all the individual event schemas
  parameters: Parameter[]; // Master list of unique properties, renamed to match backend
  isNew?: boolean; // Flag for newly created unified schema
  status: 'draft' | 'published' | 'disabled'; // Added status for schema
  type: SchemaType;
  integrationId?: string; // Optional field for linking to integrations
}

export enum SchemaType {
  CustomerSchema = 'customer_schema',
  VendorSchema = 'vendor_schema',
  PipelineSchema = 'pipeline_schema',
  UnifiedSchema = 'unified_schema',
  EventSchema = 'event_schema',
  TranslationSchema = 'translation_schema',
}

export interface FilterOptions {
  searchTerm: string;
  showGlobal: boolean;
  showEventSpecific: boolean;
  dataType?: DataType | null;
  priority?: Priority | null;
}

export interface Conflict {
  existingParam: Parameter;
  newParam: Parameter;
}

export interface NonStandardField {
  field: string;
  path: string; // Path where the non-standard field was found
}

export type ImportResolutionChoice = 'keep_existing' | 'overwrite_new' | 'ignore';

export interface ImportResolution {
  parameterResolutions: Record<string, ImportResolutionChoice>; // Map of fullPath to resolution choice
  nonStandardFieldResolutions: Record<string, ImportResolutionChoice>; // Map of field to resolution choice
}
